/**
 * rs-cart-charm-builder.js
 *
 * Cart-level charm builder edit functionality.
 * Defines two custom elements:
 *   rs-cart-charms-edit  — edit button rendered per charm line item in cart
 *   rs-cart-charm-builder — singleton drawer with full charm builder UI + cart update
 *
 * Shares .rs-charm__* CSS classes with the PDP builder (product.css).
 * Storefront API fetching uses window.RSStorefrontApiConfig (set by cart-drawer.liquid).
 * Requires rs-charm-builder-core.js (window.RsCharmBuilderBase).
 */

(() => {
  'use strict';

  const CLOSE_ICON_SVG = `<svg class="icon icon--close" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>`;

  /* ── rs-cart-charms-edit ──────────────────────────────────────────────── */

  if (!customElements.get('rs-cart-charms-edit')) {
    customElements.define('rs-cart-charms-edit', class extends HTMLElement {

      connectedCallback() {
        this.addEventListener('click', this._handleClick.bind(this));
        this.style.cursor = 'pointer';
      }

      _handleClick() {
        const scriptEl = this.querySelector('script.rs-cart-charm-data');
        if (!scriptEl) {
          console.warn('[RsCartCharmsEdit] No .rs-cart-charm-data script found inside element.');
          return;
        }

        let data;
        try {
          data = JSON.parse(scriptEl.textContent);
        } catch (err) {
          console.error('[RsCartCharmsEdit] Failed to parse charm data JSON:', err);
          return;
        }

        let builder = document.body.querySelector('rs-cart-charm-builder');
        if (!builder) {
          builder = document.createElement('rs-cart-charm-builder');
          document.body.appendChild(builder);
        }

        builder.open(data);
      }
    });
  }

  /* ── rs-cart-charm-builder ────────────────────────────────────────────── */

  if (!customElements.get('rs-cart-charm-builder')) {
    if (!window.RsCharmBuilderBase) {
      console.error('[CartCharmBuilder] RsCharmBuilderBase not loaded');
      return;
    }

    customElements.define('rs-cart-charm-builder', class extends window.RsCharmBuilderBase {
      get _root() { return this._body; }

      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this._cache = {};
        this.className = 'drawer drawer--charm-builder';
        this._buildDrawerShell();
      }

      _buildDrawerShell() {
        this.innerHTML = `
          <div class="drawer__inner" data-charm-inner data-scroll-lock-scrollable>
            <div class="drawer__head">
              <h3 class="drawer__heading">Edit Your Charms</h3>
              <button type="button" class="drawer__close" data-charm-close aria-label="Close">
                ${CLOSE_ICON_SVG}
              </button>
            </div>
            <div class="drawer__body" data-charm-body data-scroll-lock-scrollable></div>
            <div class="drawer__foot rs-cart-charm-builder__footer">
              <button type="button" class="btn btn--primary btn--outline" data-charm-cancel>Cancel</button>
              <button type="button" class="btn btn--primary btn--solid" data-charm-update>Update Cart</button>
            </div>
            <dialog class="rs-modal rs-modal--sm" data-charm-dialog data-rs-modal-self-managed aria-modal="true">
              <div class="rs-modal__body" data-dialog-body></div>
              <div class="rs-modal__actions" data-dialog-actions></div>
            </dialog>
          </div>
          <span class="underlay drawer__underlay" data-charm-underlay></span>`;

        this._inner = this.querySelector('[data-charm-inner]');
        this._body = this.querySelector('[data-charm-body]');
        this._confirmDialog = this.querySelector('[data-charm-dialog]');

        this.querySelector('[data-charm-close]').addEventListener('click', () => this._closeDrawer());
        this.querySelector('[data-charm-cancel]').addEventListener('click', () => this._closeDrawer());
        this.querySelector('[data-charm-update]').addEventListener('click', () => this._handleUpdate());
        this.querySelector('[data-charm-underlay]').addEventListener('click', () => this._closeDrawer());
      }

      open(data) {
        this._cartData = data;
        this.config = data.config;
        this.charmCount = data.charmCount;

        // Reset state for this edit session
        this.slots = new Array(this.charmCount).fill(null);
        this._renderedPanels = new Set();
        this._activeCat = 0;
        this._activeSubcat = {};
        this._pendingCharm = null;
        this._dragSrcIdx = null;
        this._caseHandler = null;

        this._buildUI();
        this._fetchAllCollections();

        if (data.currentSlots) {
          this._populateSlots(data.currentSlots);
        }

        this._openDrawer();
      }

      _buildUI() {
        this._body.innerHTML = `
          <h4 class="rs-charm__heading">${this.config.mainHeading}</h4>
          <div class="rs-charm"></div>`;

        const wrap = this._body.querySelector('.rs-charm');
        this._buildPrimaryTabs(wrap);
        this._buildPanels(wrap);
        this._buildSelectedSection(wrap);
        this._initSlots();
        this._activatePanel(0);
      }

      _populateSlots(currentSlots) {
        if (!Array.isArray(currentSlots)) return;
        currentSlots.forEach((charm, i) => {
          if (!charm || i >= this.charmCount) return;
          this.slots[i] = this._inferCharmIdentity(charm);
          const slotEl = this._slotList.querySelector(`[data-slot="${i}"]`);
          if (slotEl) this._renderSlotFilled(slotEl, i, this.slots[i]);
        });
        this._updateBadges();
      }

      _buildNewItems() {
        const { parentVariantId, parentFormProps, blankVariantId } = this._cartData;
        const ref = crypto.randomUUID?.() ?? (Date.now().toString(36) + Math.random().toString(36).slice(2, 7));

        const children = [];
        this.slots.forEach((charm, index) => {
          const props = {
            '_ref': ref,
            '_componentIndex': index,
            '_builderChild': 'true'
          };
          if (charm && charm.variantId) {
            if (charm.customization) props['Customization'] = charm.customization;
            children.push({ id: parseInt(charm.variantId), quantity: 1, properties: props });
          } else {
            children.push({ id: parseInt(blankVariantId), quantity: 1, properties: props });
          }
        });

        const parent = {
          id: parseInt(parentVariantId),
          quantity: 1,
          properties: {
            ...(parentFormProps || {}),
            '_ref': ref,
            '_componentIndex': children.length,
            '_isParentProduct': 'true',
            '_builder': 'true'
          }
        };

        return [...children, parent].reverse();
      }

      async _removeOldItems() {
        // Removing the parent is sufficient — Shopify cascades the removal to all
        // merged children automatically as part of the LinesMerge bundle.
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: this._cartData.lineKey, quantity: 0 })
        });
      }

      async _handleUpdate() {
        const updateBtn = this.querySelector('[data-charm-update]');

        const blankIndices = [];
        this.slots.forEach((slot, i) => { if (!slot) blankIndices.push(i + 1); });

        if (blankIndices.length === this.charmCount) {
          this._showBlankAlert('Please select at least one charm before updating.');
          return;
        }

        if (blankIndices.length > 0) {
          const confirmed = await this._confirmBlankSlots(blankIndices);
          if (!confirmed) return;
        }

        const newItems = this._buildNewItems();
        updateBtn.disabled = true;
        updateBtn.textContent = 'Updating…';

        try {
          await this._removeOldItems();
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: newItems })
          });
          if (!addRes.ok) throw new Error(`Add failed: ${addRes.status}`);

          // theme:cart:refresh → getCart() → fetches api-cart-items section → build() re-renders line items
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

          this._closeDrawer();
        } catch (err) {
          console.error('[RsCartCharmBuilder] Update error:', err);
          updateBtn.disabled = false;
          updateBtn.textContent = 'Update Cart';
          this._showBlankAlert('Failed to update cart. Please try again.');
        }
      }

      _openDrawer() {
        this.classList.add('is-open');

        if (window.theme?.waitForAnimationEnd && window.theme?.a11y) {
          window.theme.waitForAnimationEnd(this._inner).then(() => {
            window.theme.a11y.trapFocus(this, {
              elementToFocus: this.querySelector('[data-charm-close]')
            });
          });
        }
      }

      _closeDrawer() {
        if (!this.classList.contains('is-open')) return;

        this.classList.add('is-closing');
        this.classList.remove('is-open');

        if (window.theme?.a11y) {
          window.theme.a11y.removeTrapFocus();
          // Restore focus trap to the cart drawer if it is still open
          const cartDrawer = document.querySelector('cart-drawer.is-open');
          if (cartDrawer) {
            window.theme.a11y.trapFocus(cartDrawer, {
              elementToFocus: cartDrawer.querySelector('[data-cart-drawer-close]')
            });
          }
        }

        const cleanup = () => this.classList.remove('is-closing');
        if (window.theme?.waitForAnimationEnd) {
          window.theme.waitForAnimationEnd(this._inner).then(cleanup);
        } else {
          setTimeout(cleanup, 500);
        }
      }
    });
  }
})();
